import React, { useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import supabase from '../supabaseClient';
import { fetchProfileByAuthId } from '../api/onboarding';
import { OnboardingProvider, useOnboarding } from './OnboardingContext';
import { logOnboardingError } from '../api/errorLogger';
import { OnboardingStepper } from './Stepper';
import { WelcomeStep } from './WelcomeStep';
import { EmailSignupStep } from './EmailSignupStep';
import { FeatureLevelStep } from './FeatureLevelStep';
import { ProfileSetupStep } from './ProfileSetupStep';
import { FitnessTestStep } from './FitnessTestStep';
import { ConfirmationStep } from './ConfirmationStep';
import { NotificationsStep } from './NotificationsStep';

const stepDefinitions = [
  { id: 'welcome', label: 'Welcome', component: WelcomeStep },
  { id: 'account', label: 'Sign Up', component: EmailSignupStep },
  { id: 'tier', label: 'Choose Plan', component: FeatureLevelStep },
  { id: 'profile', label: 'Profile', component: ProfileSetupStep },
  { id: 'fitness', label: 'Fitness Test', component: FitnessTestStep },
  { id: 'confirmation', label: 'Preview', component: ConfirmationStep },
  { id: 'notifications', label: 'Stay Updated', component: NotificationsStep },
];

function isStepComplete(stepId, completion) {
  if (stepId === 'welcome' || stepId === 'account') {
    return completion.hasAccount;
  }
  if (stepId === 'profile') return completion.profileReady;
  if (stepId === 'fitness') return completion.fitnessChoice;
  return false;
}

function computeCompletion(authUser, profile) {
  const hasAccount = !!authUser;
  const profileReady = !!(
    profile &&
    profile.gender &&
    profile.date_of_birth &&
    profile.height_cm &&
    profile.weight_kg
  );
  const fitnessChoice =
    profile &&
    (profile.fitness_test_pending !== null && profile.fitness_test_pending !== undefined);

  return {
    hasAccount,
    profileReady,
    fitnessChoice: !!fitnessChoice,
  };
}

function findStartingStep(steps, completion) {
  const index = steps.findIndex((step) => !isStepComplete(step.id, completion));
  return index === -1 ? steps.length - 1 : index;
}

function OnboardingInitializer({ steps }) {
  const {
    setAuthUser,
    setProfileDraft,
    setFlags,
    markStepComplete,
    setCurrentStep,
    totalSteps,
  } = useOnboarding();
  const [initializing, setInitializing] = useState(true);
  const [statusError, setStatusError] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let bootstrapInProgress = false;

    const bootstrap = async (incomingUser, isInitialLoad = false) => {
      if (!isMounted || bootstrapInProgress) return;

      bootstrapInProgress = true;
      setInitializing(true);
      setStatusError(''); // Clear any previous errors

      try {
        let authUser = incomingUser || null;

        // Wait a bit to ensure Supabase is fully initialized
        if (!incomingUser) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        if (!authUser) {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) {
            console.error('Error retrieving Supabase session:', sessionError);
            const message = sessionError.message || 'Unable to restore session.';
            setStatusError(message);
            // Log session retrieval error
            await logOnboardingError({
              error: sessionError,
              action: 'get_session',
              metadata: { isInitialLoad },
            });
            bootstrapInProgress = false;
            return; // Exit early on session error
          }
          authUser = sessionData?.session?.user || null;
        }

        // If no authUser at this point, it means there's no active session
        // This is normal for users who haven't logged in yet or are awaiting email confirmation

        if (!isMounted) {
          bootstrapInProgress = false;
          return;
        }

        let profile = null;

        if (authUser) {
          setAuthUser(authUser);

          const { data: profileData, error: profileError } = await fetchProfileByAuthId(authUser.id);
          profile = profileData || null;

          if (profileError && profileError.code !== 'PGRST116') {
            console.error('Error fetching onboarding profile:', profileError);
            // For profile errors, we'll log them but allow the user to continue
            // They can fill in their profile information manually
            console.warn('Profile could not be loaded, user can enter details manually.');
            // Log profile fetch error (non-404 errors)
            await logOnboardingError({
              error: profileError,
              action: 'bootstrap_fetch_profile',
              authUserId: authUser?.id,
              userEmail: authUser?.email,
              metadata: { isInitialLoad },
            });
          }

          if (!isMounted) return;

          if (profileData) {
            setProfileDraft((prev) => ({
              ...prev,
              full_name: profileData.display_name || authUser.user_metadata?.full_name || '',
              gender: profileData.gender || '',
              date_of_birth: profileData.date_of_birth || '',
              height_cm: profileData.height_cm?.toString() || '',
              weight_kg: profileData.weight_kg?.toString() || '',
              activity_level: profileData.activity_level || '',
              fitness_goal: profileData.fitness_goal || '',
              email: profileData.email || authUser.email || prev.email || '',
            }));

            setFlags({
              fitness_test_pending:
                profileData.fitness_test_pending === null ||
                profileData.fitness_test_pending === undefined
                  ? null
                  : profileData.fitness_test_pending,
            });
          } else {
            setProfileDraft((prev) => ({
              ...prev,
              full_name:
                authUser.user_metadata?.full_name ||
                authUser.user_metadata?.name ||
                authUser.user_metadata?.preferred_username ||
                '',
              email: authUser.email || prev.email || '',
            }));
          }
        }

        const completion = computeCompletion(authUser, profile);

        if (completion.hasAccount) {
          markStepComplete('welcome');
          markStepComplete('account');
        }
        if (completion.profileReady) {
          markStepComplete('profile');
        }
        if (completion.fitnessChoice) {
          markStepComplete('fitness');
        }

        // Only set the starting step on initial load, not on auth state changes
        if (isInitialLoad) {
          const startingIndex = authUser ? findStartingStep(steps, completion) : 0;
          setCurrentStep(Math.min(startingIndex, totalSteps - 1));
          setHasInitialized(true);
        }
      } finally {
        if (isMounted) {
          setInitializing(false);
          bootstrapInProgress = false;
        }
      }
    };

    // Initial bootstrap with flag - only run once
    if (!hasInitialized) {
      bootstrap(null, true);
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Only re-bootstrap if already initialized (prevents loop on first load)
      if (hasInitialized) {
        // On auth state change, don't reset the step - just update auth user
        bootstrap(session?.user ?? null, false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="text-slate-300 text-sm mb-4">Loading onboarding…</div>
          {statusError && (
            <div className="max-w-md mx-auto bg-yellow-900/50 border border-yellow-700/50 text-yellow-200 rounded-lg px-4 py-3 text-sm">
              <p className="font-semibold mb-1">Note:</p>
              <p>{statusError}</p>
              <p className="mt-2 text-xs">If this persists, you can continue with a fresh start.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <OnboardingStepsRenderer steps={steps} statusError={statusError} />;
}

function OnboardingStepsRenderer({ steps }) {
  const { currentStep } = useOnboarding();
  const StepComponent = steps[currentStep]?.component || steps[0].component;
  const stepId = steps[currentStep]?.id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <OnboardingStepper steps={steps} />
      <StepComponent stepId={stepId} />
      <Outlet />
    </div>
  );
}

export function OnboardingFlow() {
  const steps = useMemo(() => stepDefinitions, []);

  return (
    <OnboardingProvider totalSteps={steps.length}>
      <OnboardingInitializer steps={steps} />
    </OnboardingProvider>
  );
}
